import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@unhead/vue';

export const APP_TITLE = 'Teamango';

const TITLE_SEPARATOR = ' · ';

type TitlePart = string | null | undefined | false;

/**
 * The inbox unread count shown as the `(N) ` prefix of the tab title. Module
 * state rather than a `useHead` entry of its own: the count is owned by
 * `MainLayout` (`useAppBadge`), but it has to be cleared on logout by code that
 * runs outside any component (`clearAppBadge`).
 */
const titleBadgeCount = ref(0);

export const setTitleBadgeCount = (count: number): void => {
  titleBadgeCount.value = count;
};

const joinTitleParts = (parts: TitlePart | TitlePart[]): string | undefined => {
  const present = [parts].flat().filter((part): part is string => !!part);

  return present.length > 0 ? present.join(TITLE_SEPARATOR) : undefined;
};

/**
 * The one place the tab title is composed: `(N) <page> · <context> · Teamango`.
 * Mounted once, in `App.vue`, so it is the lowest-priority head entry — its
 * `title` (the route's `meta.title`) is only the fallback for a page that sets
 * no title of its own, or whose own title is still loading.
 *
 * Titles go through `@unhead/vue` rather than VueUse `useTitle` because the
 * pages that matter live under MainLayout's `<KeepAlive>`: `useHead` empties a
 * deactivated page's entry and re-applies it on activation, while a `useTitle`
 * ref that did not change never rewrote `<title>` — which left a board's tab
 * reading the PREVIOUS board's name.
 */
export const useDocumentTitle = (): void => {
  const route = useRoute();

  // A getter for the whole input, not per-key values: unhead calls it inside its
  // own `watchEffect`, so the badge count read here re-renders the title. The
  // template function itself is only called later, untracked.
  useHead(() => {
    const count = titleBadgeCount.value;
    const prefix = count > 0 ? `(${count}) ` : '';

    return {
      title: route.meta.title,
      titleTemplate: (title?: string) => `${prefix}${joinTitleParts([title, APP_TITLE])}`,
    };
  });
};

/**
 * A page's own title, most specific part first: `usePageTitle(() =>
 * [savedView.value?.name ?? 'Issues', team.value?.name])`. Missing parts are
 * dropped; when every part is missing (data still loading) the route's
 * `meta.title` shows instead. A page whose title never changes needs no call —
 * `meta.title` on its route is enough.
 */
export const usePageTitle = (parts: () => TitlePart | TitlePart[]): void => {
  useHead({
    title: () => joinTitleParts(parts()),
  });
};
